import { useEffect, useState } from "react";
import { ApiError, listAllAudits, type CrossTenantAudits } from "../api/client";

/**
 * Screen 70 — all audits, all tenants.
 *
 * This is the list M5-7 deliberately did not build, and the reasoning it was withheld under
 * governs **exports** rather than reads: `breakGlass.granularity` is *one artefact, one
 * audit*, and that is still true of every export route. What the implementation
 * specification's role table says about reads is different and unconditional — a Complao
 * administrator may *"read any audit — every cross-tenant read is logged"*.
 *
 * So the list exists, and both halves of that sentence are on the screen. The warning about
 * logging is not a courtesy: opening this screen has already written an access event to
 * every certification body whose audits are on it, naming the references that were
 * disclosed, and opening a row writes another to that audit's own trail. The copy says
 * *has been*, not *will be*, because by the time it renders the writing has happened.
 *
 * **The two figures in the last column are the server's.** The percentage of the calculated
 * base and the threshold it is measured against both come from the stored determination —
 * `duration_calculations` and its adjustment ledger — through the same derivation the
 * certification body's own screen 30 reads. A browser computing "68%" from two numbers it
 * was handed would be a second derivation of a figure an assessor reads.
 *
 * An empty duration cell is an audit with no determination, not an audit at nought.
 */
export function AllAudits({
  onOpen,
}: {
  onOpen: (row: { auditId: string; tenantId: string; tenantName: string }) => void;
}) {
  const [listing, setListing] = useState<CrossTenantAudits | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAllAudits()
      .then(setListing)
      .catch((cause: unknown) =>
        setError(
          cause instanceof ApiError ? cause.message : "The audits could not be listed.",
        ),
      );
  }, []);

  const rows = listing?.values ?? [];

  return (
    <>
      <div className="eyebrow">Complao admin</div>
      <h1>All audits</h1>
      <p className="sub" style={{ marginBottom: "18px" }}>
        Across every certification body on the platform.
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {listing && listing.below_threshold > 0 ? (
        <div className="warnbar">
          <b>
            {listing.below_threshold}{" "}
            {listing.below_threshold === 1 ? "audit is" : "audits are"}
          </b>{" "}
          below the calculated duration threshold. Each carries a recorded adjustment and a
          reason on its own trail &mdash; IAF&nbsp;MD&nbsp;5 Cl.&nbsp;2.1.2 is a{" "}
          <i>should</i>, so a departure is permitted and recorded rather than refused.
        </div>
      ) : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Certification body</th>
                <th>Client</th>
                <th>Standards</th>
                <th>Status</th>
                <th>Duration vs base</th>
              </tr>
            </thead>
            <tbody>
              {listing === null ? (
                <tr>
                  <td colSpan={6}>Reading every certification body&hellip;</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>No certification body has configured an audit yet.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.audit_id}
                    className="click"
                    onClick={() =>
                      onOpen({
                        auditId: row.audit_id,
                        tenantId: row.tenant_id,
                        tenantName: row.certification_body,
                      })
                    }
                  >
                    <td>
                      <span className="mono">{row.reference}</span>
                    </td>
                    <td>
                      {row.certification_body}
                      {row.accreditation_body ? (
                        <div className="mono" style={{ fontSize: "10px" }}>
                          {`${row.accreditation_body} ${row.accreditation_number ?? ""}`.trim()}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <b>{row.client_organisation}</b>
                    </td>
                    <td>{row.standards?.length ? row.standards.join(" · ") : "—"}</td>
                    <td>
                      <span className="pill p-mute">{stateLabel(row.state)}</span>
                    </td>
                    <td>
                      {row.percent_of_calculated === null ||
                      row.percent_of_calculated === undefined ? (
                        // No determination — a draft, or one invalidated by a re-confirmation.
                        // Blank rather than 0%: nought would say the audit is at nought days.
                        "—"
                      ) : (
                        <>
                          {row.percent_of_calculated}% of base
                          {row.below_threshold ? (
                            <>
                              {" "}
                              <span className="pill p-red">Below threshold</span>
                            </>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * The `audit_state` value, in words.
 *
 * Deliberately not colour-coded by outcome. This list is a supervisory read and an audit's
 * state is not a grade — the badge colours the platform does reserve are the finding
 * outcomes', and ADR 0007 keeps those unreachable from anything but the finding itself.
 */
function stateLabel(state: string): string {
  const words = state.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
